// @ts-ignore
// https://github.com/parcel-bundler/parcel/issues/8367
// for production environments we build our scripts in commonjs format
// for commonjs parcel replaces import.meta.url with __filename, which breaks the scripts in browsers
// because of this parcel bug we currently can't use the recommended way of launching web workers
// and are using this workaround instead

import NetworkLoadFetchWorker from 'bundle-text:./helpers/NetworkLoadFetchWorker';

import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import bind from '@utils/decorators/bind';

import { ELoadingState, INetworkError, INetworkLoaderConfig, TChangeStateMetadata } from './INetworkLoader';
import { NetworkLoaderEvent } from './NetworkLoader.events';
import { ENetworkLoadFetchNetworkEvent, TNetworkLoadFetchNetworkEventPayload } from './helpers/INetworkLoadFetchWorker';
import { NetworkLoadFetch } from './helpers/NetworkLoadFetch';
import { INetworkLoadStats } from './helpers/NetworkLoadStats';

const finalStates: ELoadingState[] = [ELoadingState.ERROR, ELoadingState.LOADED, ELoadingState.ABORTED];

const workersMap: Map<string | null, Worker> = new Map();

export const getWorker: (instanceId: string | null, createIfNotExists?: boolean) => Promise<Worker | null> = async (
  instanceId: string | null = '',
  createIfNotExists?: boolean,
) => {
  let worker: Worker | undefined = workersMap.get(instanceId);

  if (!worker && createIfNotExists) {
    const blobURL: string = URL.createObjectURL(
      new Blob([`${NetworkLoadFetchWorker}`], {
        type: 'application/javascript',
      }),
    );

    // the workaround for workers does not work with parcel dev server
    // so for dev environment we're using the recommended way instead of the workaround
    if (process.env.NODE_ENV === 'development') {
      // @ts-ignore
      worker = new Worker(new URL('helpers/NetworkLoadFetchWorker.ts', import.meta.url), { type: 'module' });
    } else {
      worker = new Worker(blobURL);
    }

    if (worker) {
      workersMap.set(instanceId, worker);
    } else {
      throw new Error();
    }
  }

  return worker || null;
};

export const destroyWorker: (instanceId: string | null) => Promise<void> = async (instanceId: string | null) => {
  const w: Worker | null = await getWorker(instanceId);

  if (w) {
    w.terminate();
  }

  workersMap.delete(instanceId);
};

export class NetworkLoader {
  public _eventBus: TEventBus;
  public data: ArrayBuffer | string | null = null;
  public error: INetworkError | null = null;
  public stats: INetworkLoadStats | null = null;
  public realUrl: string = '';
  private _state: ELoadingState = ELoadingState.UNKNOWN;
  private _retriesAmount: number = 0;
  private _timeoutAbortHandler: ReturnType<typeof globalThis.setTimeout> | null = null;
  private _retryTimeoutHandler: ReturnType<typeof globalThis.setTimeout> | null = null;
  private readonly _config: INetworkLoaderConfig;
  private readonly _logger: TLogger;
  private _fetcherWorker: Worker | null = null;
  private _fetcherSimple: NetworkLoadFetch | null = null;
  private _workerMode: boolean = false;

  constructor(config: INetworkLoaderConfig, eventBus: TEventBus) {
    this._eventBus = eventBus;

    this._logger = Logger(this._eventBus, 'Doppio - NetworkLoader');

    if (!config.url || !config.onStateChanged) {
      throw new Error('Invalid config');
    }

    this._workerMode = !!config.workerMode;
    this._config = config;
    this._logger.debug('Created with config', config);

    this._recreateFetcherAndAutoload();
  }

  public startLoad(): boolean {
    if (this._state !== ELoadingState.UNKNOWN) {
      return false;
    }

    this._armTimeoutAbort();
    this._logger.debug(`Starting load ${this._config.url}`);

    this._startAsyncLoad();

    return true;
  }

  public cancel(reason?: string): void {
    this._disarmTimeoutAbort();
    this._disarmRetryTimeout();

    this.error = {
      status: -1,
      statusText: `Cancelled because of ${reason ?? 'unknown reason'}`,
    };

    if (finalStates.includes(this._state)) {
      return;
    }

    this._logger.warn(`${this._config.url} loading was cancelled with reason ${reason}, current status is ${this._state}`);

    if (this._workerMode) {
      this._fetcherWorker?.postMessage({
        type: ENetworkLoadFetchNetworkEvent.ABORT,
        payload: { url: this._config.url.toString() },
      } as TNetworkLoadFetchNetworkEventPayload);
    } else {
      this._fetcherSimple?.abort();
    }
  }

  public destroy(): void {
    if (!finalStates.includes(this._state)) {
      this.cancel('Destroyed');
      this._state = ELoadingState.DESTROYED;
    }
    this._disarmTimeoutAbort();
    this._disarmRetryTimeout();

    this.data = null;
    this.error = null;

    this._removeFetcherFromWorker();
  }

  private _onAbortTimeoutFired(): void {
    this._logger.info(`${this._config.url} loading timeout`);
    this.cancel('Loading timeout');
  }

  private _armRetryTimeout(): void {
    this._logger.info(`Retry loading ${this._retriesAmount} time`);

    this._disarmRetryTimeout();
    this._setNewState(ELoadingState.UNKNOWN);
    this._retriesAmount++;
    this._retryTimeoutHandler = globalThis.setTimeout(() => {
      this.startLoad();
    }, 2 ** this._retriesAmount);
  }

  private _disarmRetryTimeout(): void {
    if (this._retryTimeoutHandler) {
      globalThis.clearTimeout(this._retryTimeoutHandler);
    }
  }

  private _armTimeoutAbort(): void {
    if (!this._config.loadingTimeout || this._config.loadingTimeout <= 0) {
      this._logger.debug(`LoadingTimeout is not defined or below zero! Skipping abort timeout arming`);
      return;
    }

    this._logger.debug('Arming loading abort timeout');

    if (this._timeoutAbortHandler) {
      this._logger.debug('Another timeout already armed. Cancel it');
      this._disarmTimeoutAbort();
    }

    this._timeoutAbortHandler = setTimeout(() => {
      this._onAbortTimeoutFired();
    }, this._config.loadingTimeout);
  }

  private _disarmTimeoutAbort(): void {
    if (this._timeoutAbortHandler) {
      this._logger.debug('Disarming abort timeout');
      clearTimeout(this._timeoutAbortHandler);
    }
  }

  @bind
  private _setNewState(newState: ELoadingState, metadata?: TChangeStateMetadata): void {
    if (this._state === newState) {
      return;
    }

    if (newState === ELoadingState.LOADED) {
      this._logger.verbose(`${this._config.url} loading finished`);
    }

    if (finalStates.includes(newState)) {
      this._eventBus.publish(NetworkLoaderEvent.events.networkRequestSettled, {
        response: {
          url: this.url,
          body: this.data,
          timestamp: performance.now(),
          ...(metadata || {}),
        },
      });
      this._removeFetcherFromWorker();
      this._disarmTimeoutAbort();
    }

    this._logger.verbose(`Loading ${this._config.url}, state transition: ${this._state} -> ${newState}`);

    if (
      [ELoadingState.ABORTED, ELoadingState.ERROR].includes(newState) &&
      this._state !== ELoadingState.DESTROYED &&
      this._config.maxRetries &&
      this._config.baseRetryTimeout &&
      this._retriesAmount < this._config.maxRetries
    ) {
      this._armRetryTimeout();
      return;
    }

    this._config.onStateChanged(this._state, newState, this);
    this._state = newState;
  }

  private _removeFetcherFromWorker(): void {
    this._fetcherWorker?.removeEventListener('message', this._handleWorkerMessage);

    this._fetcherWorker?.postMessage({
      type: ENetworkLoadFetchNetworkEvent.DESTROY,
      payload: { url: this._config.url.toString() },
    } as TNetworkLoadFetchNetworkEventPayload);
  }

  private async _startAsyncLoad(): Promise<void> {
    if (this._workerMode) {
      const fetchConfig: TNetworkLoadFetchNetworkEventPayload = {
        type: ENetworkLoadFetchNetworkEvent.FETCH,
        payload: { url: this._config.url.toString(), mode: this._config.mode, requestCustomReferer: this._config.requestCustomReferer },
      };
      this._fetcherWorker?.postMessage(fetchConfig);
    } else {
      this._fetcherSimple?.fetch(this._config.url.toString(), this._config.mode);
    }
  }

  private async _recreateFetcherAndAutoload(): Promise<void> {
    this._fetcherSimple?.abort();
    this._fetcherWorker?.postMessage({ type: ENetworkLoadFetchNetworkEvent.DESTROY, payload: { url: this._config.url } });

    if (this._workerMode) {
      try {
        this._fetcherWorker = await getWorker(this._eventBus.getAppId(), true);
        this._fetcherWorker?.addEventListener('message', this._handleWorkerMessage);
      } catch (e) {
        this._logger.warn('Could not use worker for network request. Fallback to simple mode', { error: e });
        this._workerMode = false;
        this._recreateFetcherAndAutoload();
        return;
      }
    } else {
      this._fetcherSimple = new NetworkLoadFetch({
        requestCustomReferer: this._config.requestCustomReferer,
        onDataReady: this._handleDataReady,
        onChangeState: this._setNewState,
        onStatsReady: this._handleLoadStatsReady,
        onError: this._handleError,
        onRedirectDetected: this._handleRedirectDetected,
      });
    }

    if (this._config.autoLoad) {
      this._logger.debug('AutoLoad config field set to true. Starting load');

      globalThis.queueMicrotask(() => {
        this.startLoad();
      });
    }
  }

  @bind
  private _handleDataReady(data: ArrayBuffer | string): void {
    this.data = data;
  }

  @bind
  private _handleLoadStatsReady(stats: INetworkLoadStats): void {
    this.stats = stats;
    this._eventBus.publish(NetworkLoaderEvent.stats.statsReady, stats);
  }

  @bind
  private _handleError(error: INetworkError): void {
    this.error = error;
    this._logger.warn(`Cannot load ${this._config.url}`, error);
  }

  @bind
  private _handleRedirectDetected(requestUrl: string, responseUrl: string): void {
    this._eventBus.publish(NetworkLoaderEvent.events.browserRedirectDetected, {
      requestUrl,
      responseUrl,
    });
  }

  @bind
  private _handleWorkerMessage({ data }: MessageEvent<TNetworkLoadFetchNetworkEventPayload>): void {
    if (data.payload.url !== this._config.url.toString()) {
      return;
    }

    if (data.type) {
      switch (data.type) {
        case ENetworkLoadFetchNetworkEvent.DATA_READY: {
          this._handleDataReady(data.payload.binaryData);
          break;
        }

        case ENetworkLoadFetchNetworkEvent.CHANGE_STATE: {
          this._setNewState(data.payload.newState, data.payload.metadata);
          break;
        }

        case ENetworkLoadFetchNetworkEvent.STATS_READY: {
          this._handleLoadStatsReady(data.payload.stats);
          break;
        }

        case ENetworkLoadFetchNetworkEvent.ERROR: {
          this._handleError(data.payload.error);
          break;
        }

        case ENetworkLoadFetchNetworkEvent.REDIRECT_DETECTED: {
          this._handleRedirectDetected(data.payload.url, data.payload.responseUrl);
          break;
        }
      }
    }
  }

  get url(): string {
    return this._config.url.toString();
  }

  get queryParams(): URLSearchParams {
    return this._config.url.searchParams;
  }
}
